import { useMemo, useState } from 'react'

import { CellClassRules } from 'ag-grid-enterprise'
import { CalendarCheck, CheckLine, Eye, EyeOff, RotateCcw } from 'lucide-react'
import { renderToString } from 'react-dom/server'

import { getTMinus1, getTMinus2, toISO8601 } from '@fortem-monorepo/js-utilities'
import { numberFormatter } from '@fortem-monorepo/js-utilities'
import { CustomMenuItem } from '@fortem-monorepo/react-ag'
import { createColDefs, createContextMenu } from '@fortem-monorepo/react-ag'
import { CustomAgGrid } from '@fortem-monorepo/react-ag'
import { useResizableState } from '@fortem-monorepo/react-hooks'
import { HeaderConfigBar } from '@fortem-monorepo/react-spa'
import { Widget, WidgetContent, WidgetHeaderWithQuery } from '@fortem-monorepo/react-spa'
import { useUserContext } from '@fortem-monorepo/react-spa'
import { DatePicker } from '@fortem-monorepo/react-ui'
import { SearchSelect } from '@fortem-monorepo/react-ui'
import { Button } from '@fortem-monorepo/react-ui'
import { FormItem } from '@fortem-monorepo/react-ui'
import { Label } from '@fortem-monorepo/react-ui'
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@fortem-monorepo/react-ui'
import { Prices } from '@fortem-monorepo/web-sdk'

import { Page } from '@/components/Page'
import { OPERATIONS } from '@/constants/links'
import {
  useResetPriceValidationEntryMutation,
  useUpdatePriceValidationEntryMutation,
  useValidateAllPricesMutation,
  useValidatePriceValidationEntryMutation,
} from '@/react-query/mutations/price-validation'
import { useFunds } from '@/react-query/queries/funds/funds'
import {
  usePriceValidationImpactQuery,
  usePriceValidationQuery,
} from '@/react-query/queries/prices/prices'

type UserContext = ReturnType<typeof useUserContext>

const getPriceValidationColDefs = (userContext: UserContext) => {
  const canEdit = userContext.hasPermissions((permissions) => ({
    permissions: [permissions.WRITE_PRICE_VALIDATION],
  }))

  return createColDefs([
    {
      field: 'fund_name',
      hide: true,
    },
    {
      field: 'id',
      hide: true,
    },
    {
      field: 'value_date',
      rowGroup: true,
      width: 115,
    },
    {
      field: 'structurename',
      width: 250,
    },
    {
      field: 'price',
      headerName: 'Px',
      width: 115,
      filter: false,
      valueFormatter: numberFormatter({
        delimiter: true,
        maximumFractionDigits: 4,
        minimumFractionDigits: 4,
      }),
    },
    {
      field: 'ops_price',
      headerName: 'Ops Px',
      width: 115,
      filter: false,
      editable: canEdit,
      valueFormatter: numberFormatter({
        delimiter: true,
        maximumFractionDigits: 4,
        minimumFractionDigits: 4,
      }),
    },
    {
      field: 'provider',
      headerName: 'Bank',
      width: 115,
    },
    {
      field: 'comment',
      headerName: 'Comment',
      width: 220,
      editable: canEdit,
    },

    {
      headerName: 'Proxy',
      groupId: 'Proxy',
      children: [
        {
          field: 'proxy_price',
          headerName: 'Proxy Px',
          width: 110,
          filter: false,
          valueFormatter: numberFormatter({ delimiter: true, minimumFractionDigits: 4 }),
        },
        {
          field: 'proxy_diff_bps',
          headerName: 'Diff Bps',
          width: 110,
          filter: false,
          valueFormatter: numberFormatter({
            delimiter: true,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
            // suffix: "bps",
          }),
        },

        {
          field: 'notional',
          headerName: 'Notional',
          width: 110,
          filter: false,
          valueFormatter: numberFormatter({ delimiter: true, minimumFractionDigits: 0 }),
          columnGroupShow: 'open',
        },
        {
          field: 'cash_flow',
          headerName: 'CF',
          width: 110,
          filter: false,
          valueFormatter: numberFormatter({ delimiter: true, maximumFractionDigits: 0 }),
          columnGroupShow: 'open',
        },
        {
          field: 'sum_delta_perf',
          headerName: 'Delta Perf',
          width: 110,
          filter: false,
          valueFormatter: numberFormatter({ delimiter: true, minimumFractionDigits: 19 }),
          columnGroupShow: 'open',
        },
        {
          field: 'base_proxy_price',
          headerName: 'Px T-1',
          width: 110,
          filter: false,
          valueFormatter: numberFormatter({ delimiter: true, minimumFractionDigits: 4 }),
          columnGroupShow: 'open',
        },
      ],
    },
  ])
}

const red_green_rules: CellClassRules<Prices.GetPriceValidationImpactData[number]> = {
  'text-amber-600 font-medium': (params) => {
    const x = params.value
    return (x > 1 && x < 2) || (x < -1 && x > -2)
  },
  'text-red-600 font-medium': (params) => {
    const x = params.value
    return x >= 2 || x <= -2
  },
}

const getPriceValidationImpactColDefs = () => {
  return createColDefs([
    {
      field: 'fund_name',
      headerName: 'Fund',
      width: 100,
    },
    {
      field: 'instrument_id',
      hide: true,
    },
    {
      field: 'instrument_type',
      rowGroup: true,
      hide: true,
      width: 115,
    },
    {
      field: 'value_date',
      headerName: 'Value Date',
      width: 125,
    },
    {
      field: 'unique_identifier',
      headerName: 'Product',
      width: 150,
    },
    {
      field: 'difference_bps',
      headerName: 'Diff Bps',
      width: 110,
      filter: false,
      cellClassRules: red_green_rules,
      valueFormatter: numberFormatter({
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
        suffix: ' bp',
        transformer: (val) => Math.ceil(val * 10) / 10,
      }),
    },

    {
      headerName: 'Ops',
      groupId: 'Proxy',
      children: [
        {
          field: 'ops_price_t',
          headerName: 'Price',
          width: 110,
          filter: false,
          cellStyle: { textAlign: 'right' },
          valueFormatter: numberFormatter({ maximumFractionDigits: 5, delimiter: true }),
        },
        {
          field: 'ops_pnl',
          headerName: 'P&L',
          width: 110,
          filter: false,
          cellStyle: { textAlign: 'right' },
          valueFormatter: numberFormatter({
            delimiter: true,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }),
        },

        {
          field: 'ops_nav',
          headerName: 'NAV',
          width: 125,
          filter: false,
          cellStyle: { textAlign: 'right' },
          valueFormatter: numberFormatter({
            delimiter: true,
            maximumFractionDigits: 0,
            minimumFractionDigits: 0,
          }),
          columnGroupShow: 'closed',
        },
        {
          field: 'ops_pnl_over_nav_bps',
          headerName: 'P&L/NAV bps',
          width: 115,
          filter: false,
          cellStyle: { textAlign: 'right' },
          valueFormatter: numberFormatter({
            delimiter: true,
            maximumFractionDigits: 2,
            suffix: ' bp',
          }),
          columnGroupShow: 'closed',
        },
        {
          field: 'ops_position_weight_pct',
          headerName: 'Wgt',
          width: 95,
          filter: false,
          cellStyle: { textAlign: 'right' },
          valueFormatter: numberFormatter({
            maximumFractionDigits: 2,
            delimiter: true,
            suffix: '%',
          }),
          columnGroupShow: 'closed',
        },
        {
          field: 'ops_performance_1day_pct',
          headerName: 'IdxPerf',
          width: 100,
          filter: false,
          cellStyle: { textAlign: 'right' },
          valueFormatter: numberFormatter({
            maximumFractionDigits: 2,
            delimiter: true,
            suffix: '%',
          }),
          columnGroupShow: 'closed',
        },
        {
          field: 'ops_wgt_time_index_perf_bps',
          headerName: 'Wgt * IdxPerf bps',
          width: 160,
          filter: false,
          cellStyle: { textAlign: 'right' },
          valueFormatter: numberFormatter({ delimiter: true, maximumFractionDigits: 2 }),
          columnGroupShow: 'closed',
        },
        {
          field: 'ops_difference_bps',
          headerName: 'Diff Bps',
          width: 100,
          filter: false,
          cellClassRules: red_green_rules,
          cellStyle: { textAlign: 'right' },
          valueFormatter: numberFormatter({ delimiter: true, maximumFractionDigits: 2 }),
          columnGroupShow: 'closed',
        },
      ],
    },

    {
      headerName: 'Proxy',
      groupId: 'Proxy',
      children: [
        {
          field: 'prx_price_t',
          headerName: 'Price',
          width: 110,
          filter: false,
          cellStyle: { textAlign: 'right' },
          valueFormatter: numberFormatter({
            maximumFractionDigits: 5,
            minimumFractionDigits: 5,
            delimiter: true,
          }),
        },
        {
          field: 'prx_pnl',
          headerName: 'P&L',
          width: 110,
          filter: false,
          cellStyle: { textAlign: 'right' },
          valueFormatter: numberFormatter({
            maximumFractionDigits: 0,
            minimumFractionDigits: 0,
            delimiter: true,
          }),
        },

        {
          field: 'prx_nav',
          headerName: 'NAV',
          width: 125,
          filter: false,
          cellStyle: { textAlign: 'right' },
          valueFormatter: numberFormatter({
            maximumFractionDigits: 0,
            minimumFractionDigits: 0,
            delimiter: true,
          }),
          columnGroupShow: 'closed',
        },
        {
          field: 'prx_pnl_over_nav_bps',
          headerName: 'P&L/NAV bps',
          width: 115,
          filter: false,
          cellStyle: { textAlign: 'right' },
          valueFormatter: numberFormatter({
            delimiter: true,
            maximumFractionDigits: 2,
            suffix: ' bp',
          }),
          columnGroupShow: 'closed',
        },
        {
          field: 'prx_position_weight_pct',
          headerName: 'Wgt',
          width: 95,
          filter: false,
          cellStyle: { textAlign: 'right' },
          valueFormatter: numberFormatter({
            delimiter: true,
            suffix: '%',
            maximumFractionDigits: 2,
          }),
          columnGroupShow: 'closed',
        },
        {
          field: 'prx_performance_1day_pct',
          headerName: 'IdxPerf',
          width: 100,
          filter: false,
          cellStyle: { textAlign: 'right' },
          valueFormatter: numberFormatter({
            delimiter: true,
            suffix: '%',
            maximumFractionDigits: 2,
          }),
          columnGroupShow: 'closed',
        },
        {
          field: 'prx_wgt_time_index_perf_bps',
          headerName: 'Wgt * IdxPerf bps',
          width: 160,
          filter: false,
          cellStyle: { textAlign: 'right' },
          valueFormatter: numberFormatter({ delimiter: true, maximumFractionDigits: 2 }),
          columnGroupShow: 'closed',
        },
        {
          field: 'prx_difference_bps',
          headerName: 'Diff Bps',
          width: 100,
          filter: false,
          cellClassRules: red_green_rules,
          cellStyle: { textAlign: 'right' },
          valueFormatter: numberFormatter({ delimiter: true, maximumFractionDigits: 2 }),
          columnGroupShow: 'closed',
        },
      ],
    },
  ])
}

export interface AutomatedPricingPageProps {}

export const AutomatedPricingPage: React.FC<AutomatedPricingPageProps> = ({}) => {
  const userContext = useUserContext()

  const [validation, validationImpact] = useResizableState(2)

  const hasWriteAccess = userContext.hasPermissions((permissions) => ({
    permissions: [permissions.WRITE_PRICE_VALIDATION],
  }))

  const [fund_name, setFundName] = useState('')
  const [value_date, setValueDate] = useState(getTMinus1())
  const [value_date_mo, setValueDateTMinus1] = useState(getTMinus2())

  const params = {
    fund_name,
    value_date: toISO8601(value_date),
    value_date_mo: toISO8601(value_date_mo),
  }

  const { data: funds, query: fundsQuery } = useFunds()
  const validationQuery = usePriceValidationQuery(params)
  const validationImpactQuery = usePriceValidationImpactQuery(params)

  const updateRowMutation = useUpdatePriceValidationEntryMutation()
  const resetRowMutation = useResetPriceValidationEntryMutation()
  const validateRowMutation = useValidatePriceValidationEntryMutation()
  const validateAllPricesMutation = useValidateAllPricesMutation()

  const validationData = useMemo(() => {
    return validationQuery.data ?? []
  }, [validationQuery.data])

  const impactData = useMemo(() => {
    return validationImpactQuery.data ?? []
  }, [validationImpactQuery.data])

  const fundsOptions = useMemo(() => {
    return funds.map((fund) => ({
      label: fund.fund_name,
      value: fund.fund_name,
    }))
  }, [funds])

  // useEffect(() => {
  //   if (funds.length > 0 && !fund_name) {
  //     setFundName(funds[0].fund_name)
  //   }
  // }, [funds])

  const inputsDisabled = [fundsQuery, validationQuery, validationImpactQuery].some(
    (query) => query.isFetching,
  )

  const priceValidationColDefs = useMemo(() => {
    return getPriceValidationColDefs(userContext)
  }, [userContext])

  const priceValidationImpactColDefs = useMemo(() => {
    return getPriceValidationImpactColDefs()
  }, [])

  const getValidationContextMenuItems = createContextMenu<Prices.GetPriceValidationData[number]>(
    (params) => {
      const structureName = params.node!.data?.structurename
      const entry_id = params.node!.data!.id

      const items: CustomMenuItem[] = [...(params.defaultItems as CustomMenuItem[])]

      if (hasWriteAccess && !params.node!.group) {
        items.unshift('separator')
        items.unshift({
          name: `Validate all Prices - ${toISO8601(value_date)}`,
          action: () => {
            return validateAllPricesMutation.mutateAsync({
              fund_name,
              value_date: toISO8601(value_date),
            })
          },
          icon: renderToString(<CalendarCheck size={16} />),
        } satisfies CustomMenuItem)
        items.unshift({
          name: `Validate Row - ${structureName}`,
          action: () => {
            return validateRowMutation.mutateAsync({ entry_id })
          },
          icon: renderToString(<CheckLine size={16} />),
        } satisfies CustomMenuItem)
        items.unshift('separator')
        items.unshift({
          name: `Reset Row - ${structureName}`,
          action: () => {
            return resetRowMutation.mutateAsync({ entry_id })
          },
          icon: renderToString(<RotateCcw size={16} />),
          cssClasses: ['bg-red-400/10!', 'hover:bg-red-400/20!'],
        })
      }

      return items
    },
  )

  return (
    <Page
      metadata={{ title: 'Automated Pricing' }}
      permissions={OPERATIONS.AUTOMATED_PRICING.permissions}
    >
      <HeaderConfigBar>
        <div className="flex items-center gap-2">
          <FormItem>
            <Label className={'text-gray-500 font-medium text-xs'}>Value Date</Label>
            <DatePicker
              size="sm"
              buttonDisabled={inputsDisabled}
              selected={value_date}
              onSelect={(date) => {
                date && setValueDate(date)
              }}
            />
          </FormItem>
          <FormItem>
            <Label className={'text-gray-500 font-medium text-xs'}>Value Date - 1</Label>
            <DatePicker
              size="sm"
              buttonDisabled={inputsDisabled}
              selected={value_date_mo}
              onSelect={(date) => {
                date && setValueDateTMinus1(date)
              }}
            />
          </FormItem>
          <FormItem>
            <Label className={'text-gray-500 font-medium text-xs'}>Fund</Label>
            <SearchSelect
              size="sm"
              loading={fundsQuery.isFetching}
              options={fundsOptions}
              disabled={inputsDisabled}
              value={fund_name}
              setValue={setFundName}
              placeholder={'Select a fund...'}
              className={'min-w-auto! w-40!'}
            />
          </FormItem>
          <div className={'flex flex-col gap-2'}>
            <Label className={'text-gray-500 font-medium text-xs'}>Show / Hide</Label>
            <div className="flex items-center gap-2 mt-auto">
              <Button size="sm" variant="outline" onClick={() => validation.toggle()}>
                Validation
                {validation.open ? <Eye /> : <EyeOff />}
              </Button>
              <Button size="sm" variant="outline" onClick={() => validationImpact.toggle()}>
                Impact
                {validationImpact.open ? <Eye /> : <EyeOff />}
              </Button>
            </div>
          </div>
        </div>
      </HeaderConfigBar>
      <ResizablePanelGroup direction={'vertical'} autoSaveId={'conditional'}>
        {validation.open && (
          <ResizablePanel defaultSize={70} minSize={10} id="validation-panel" order={1}>
            <Widget exposed className={'h-full'}>
              <WidgetHeaderWithQuery query={validationQuery} title="Price Validation" />
              <WidgetContent loading={validationQuery?.isLoading}>
                <CustomAgGrid
                  domLayout={'normal'}
                  getContextMenuItems={getValidationContextMenuItems}
                  onCellValueChanged={(params: any) => {
                    return updateRowMutation.mutateAsync({
                      rowData: params.node.data,
                      oldValue: params.oldValue,
                      newValue: params.newValue,
                      updatedField: params.colDef.field!,
                    })
                  }}
                  columnDefs={priceValidationColDefs}
                  rowData={validationData}
                />
              </WidgetContent>
            </Widget>
          </ResizablePanel>
        )}
        {validation.open && validationImpact.open && <ResizableHandle />}
        {validationImpact.open && (
          <ResizablePanel defaultSize={30} minSize={10} id="impact-panel" order={2}>
            <Widget exposed className={'h-full'}>
              <WidgetHeaderWithQuery
                query={validationImpactQuery}
                title="Price Validation Impact"
              />
              <WidgetContent loading={validationImpactQuery?.isLoading}>
                <CustomAgGrid
                  domLayout={'normal'}
                  columnDefs={priceValidationImpactColDefs}
                  rowData={impactData}
                />
              </WidgetContent>
            </Widget>
          </ResizablePanel>
        )}
      </ResizablePanelGroup>
    </Page>
  )
}
